import styled, { css } from 'styled-components';
import { isValidImageUrl } from '@anwb/forms';
import Icon, { ICON_SIZES } from '@anwb/icon';
import { pxToRem } from '@anwb/style-utilities';
import Typography from '@anwb/typography';
const CHECKBOX_SPACING_TOP = 3;
const CHECKBOX_CONTENT_SIZE = 44;
const CHECKBOX_IMAGE_HEIGHT = 12;
const CHECKBOX_BOX_SIZE = 18;
const CHECKBOX_HITBOX_OFFSET_HORIZONTAL = 6;
const CHECKBOX_HITBOX_OFFSET_VERTICAL = 10;
export const checkboxContainerVariantStyles = ({ theme, $variant, }) => css `
	display: flex;

	&:hover {
		${CheckboxInput}:not([disabled]):not(:checked) {
			+ ${CheckboxSize}, + ${CheckboxColor} {
				border-color: ${theme.colors.highlight.borderSelected};
				outline: 0;
			}
		}
	}

	${CheckboxInput}:focus {
		+ ${CheckboxSize}, + ${CheckboxColor} {
			border-color: ${theme.colors.highlight.borderSelected};
		}

		+ ${CheckboxLabelBasic}::before {
			border-color: ${theme.colors.highlight.borderSelected};
		}
	}

	${CheckboxInput}:checked {
		+ ${CheckboxSize}, + ${CheckboxColor} {
			border-color: ${theme.colors.action.borderClicked};
		}

		+ ${CheckboxLabelBasic} {
			&::before {
				color: ${theme.colors.highlight.fillRadioButton};
			}

			${CheckboxIcon} {
				display: block;
			}
		}

		:focus + ${CheckboxLabelBasic}::before {
			border-color: ${theme.colors.highlight.borderSelected};
		}
	}

	${$variant === 'size' &&
    css `
		${checkboxContainerVariantSizeStyles}
	`}

	${$variant === 'color' &&
    css `
		${checkboxContainerVariantColorStyles}
	`}

	input[type="checkbox"]:checked+span::before {
		background-image: none;
	}
`;
export const checkboxContainerVariantSizeStyles = ({ theme }) => css `
	${CheckboxInput}:checked + ${CheckboxSize} {
		span {
			color: ${theme.colors.action.textActive};
		}
	}

	${CheckboxInput}:disabled + ${CheckboxSize} {
		background-color: ${theme.colors.extralight.bgFormFieldDisabled};

		span {
			color: ${theme.colors.medium.textInactive};
		}
	}
`;
export const checkboxContainerVariantColorStyles = () => css `
	${CheckboxInput}:disabled + ${CheckboxColor} ${CheckboxColorContent} {
		opacity: 0.5;
	}
`;
export const CheckboxContainer = styled.label `
	${checkboxContainerVariantStyles}
`;
/**
 * Checkbox color
 */
export const checkboxColorStyles = ({ theme }) => css `
	position: relative;
	height: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	width: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	border: ${pxToRem(theme.borderWidth[2])} solid ${theme.colors.medium.borderFormField};
	border-radius: ${pxToRem(theme.borderRadius.m)};
	box-sizing: border-box;
	overflow: hidden;
`;
export const CheckboxColor = styled.div `
	${checkboxColorStyles}
`;
export const checkboxColorContentStyles = ({ theme, $value, }) => css `
	position: absolute;
	top: -${pxToRem(theme.borderWidth[2])};
	left: -${pxToRem(theme.borderWidth[2])};
	height: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	width: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	border: ${pxToRem(theme.borderWidth[2])} solid ${theme.colors.medium.borderFormField};
	box-shadow: inset 0 0 0 ${pxToRem(theme.borderWidth[2])} ${theme.colors.blanc.bgFormField};
	border-radius: ${pxToRem(theme.borderRadius.m)};
	box-sizing: border-box;

	${isValidImageUrl($value) ? `background-image: url(${$value});` : `background-color: ${$value};`}
`;
export const CheckboxColorContent = styled.div `
	${checkboxColorContentStyles}
`;
/**
 * Checkbox size
 */
export const checkboxSizeStyles = ({ theme }) => css `
	display: flex;
	justify-content: center;
	align-items: center;
	width: auto;
	height: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	min-width: ${pxToRem(CHECKBOX_CONTENT_SIZE)};
	box-sizing: border-box;
	text-align: center;
	border: ${pxToRem(theme.borderWidth[2])} solid ${theme.colors.medium.borderFormField};
	box-shadow: inset 0 0 0 ${pxToRem(theme.borderWidth[2])} ${theme.colors.blanc.bgFormField};
	border-radius: ${pxToRem(theme.borderRadius.m)};
	padding: 0 ${pxToRem(theme.spacing[200])};
`;
export const CheckboxSize = styled.div `
	${checkboxSizeStyles}
`;
/**
 * Checkbox size text
 */
export const checkboxSizeTextStyles = ({ theme }) => css `
	line-height: ${pxToRem(theme.lineHeights[400])};
`;
export const CheckboxSizeText = styled(Typography).attrs({
    variant: 'button-link',
}) `
	${checkboxSizeTextStyles}
`;
export const checkboxInputStyles = ({ theme, $isInvalid, }) => css `
	width: ${pxToRem(theme.borderWidth[2])};
	height: ${pxToRem(theme.borderWidth[2])};
	padding: 0;
	margin: -1px;
	overflow: hidden;
	position: absolute;
	clip: rect(0, 0, 0, 0);

	&:not([disabled]):hover + ${CheckboxLabelBasic} {
		cursor: pointer;
	}

	// CORE-UI override
	&& {
		&:disabled + ${CheckboxLabelBasic}::before {
			background-color: ${theme.colors.extralight.bgFormFieldDisabled};
			border-color: ${theme.colors.light.borderFormFieldDisabled};
		}
	}

	${$isInvalid &&
    css `
		&&& {
			+ ${CheckboxLabelBasic}::before, + ${CheckboxColor}, + ${CheckboxSize} {
				border-color: ${theme.colors.error.borderFormField};
			}

			&:focus + ${CheckboxLabelBasic}::before {
				border-color: ${theme.colors.error.borderFormField};
			}
		}
	`}
`;
export const CheckboxInput = styled.input.attrs({ type: 'checkbox' }) `
	${checkboxInputStyles}
`;
/**
 * Checkbox label basic
 */
export const checkboxLabelBasicStyles = ({ theme }) => css `
	position: relative;
	display: block;
	// CORE-UI overwrite
	&& {
		padding-left: ${pxToRem(Number(theme.fontSize[600]) + Number(theme.spacing[200]))};
	}
	position: relative;
	min-height: ${pxToRem(Number(theme.fontSize[600]) + CHECKBOX_SPACING_TOP)};

	&&& {
		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			box-sizing: border-box;

			display: flex !important;
			align-items: center;
			justify-content: center;

			width: ${pxToRem(CHECKBOX_BOX_SIZE)};
			height: ${pxToRem(CHECKBOX_BOX_SIZE)};
			margin-top: ${pxToRem(CHECKBOX_SPACING_TOP)};

			background: ${theme.colors.blanc.bgFormField};
			background-image: none;
			font-size: ${pxToRem(CHECKBOX_IMAGE_HEIGHT)};
			color: ${theme.colors.blanc.bgFormField};

			border: ${pxToRem(theme.borderWidth[2])} solid ${theme.colors.medium.borderFormField};
		}
	}

	/* increased hitbox */
	&::after {
		content: '';
		position: absolute;
		top: -${pxToRem(CHECKBOX_HITBOX_OFFSET_VERTICAL)};
		left: -${pxToRem(CHECKBOX_HITBOX_OFFSET_HORIZONTAL)};
		width: calc(
			${pxToRem(CHECKBOX_BOX_SIZE)} + (2 * ${pxToRem(CHECKBOX_HITBOX_OFFSET_HORIZONTAL)}) +
				(2 * ${pxToRem(theme.borderWidth[2])})
		);
		height: calc(
			${pxToRem(CHECKBOX_BOX_SIZE)} + (2 * ${pxToRem(CHECKBOX_SPACING_TOP)}) +
				(2 * ${pxToRem(CHECKBOX_HITBOX_OFFSET_VERTICAL)}) + (2 * ${pxToRem(theme.borderWidth['2'])})
		);
	}
`;
export const CheckboxLabelBasic = styled(Typography).attrs({
    variant: 'body-text',
}) `
	${checkboxLabelBasicStyles}
`;
/**
 * Checkbox icon
 */
export const checkboxIconStyles = ({ theme }) => css `
	display: none;
	position: absolute;
	top: ${pxToRem(CHECKBOX_BOX_SIZE / 2 + 3)};
	left: ${pxToRem(CHECKBOX_BOX_SIZE / 2)};
	transform: translate(-50%, -50%);
	height: ${pxToRem(ICON_SIZES.xs)};
	width: ${pxToRem(ICON_SIZES.xs)};
	color: ${theme.colors.highlight.iconFormField};
`;
export const CheckboxIcon = styled(Icon).attrs({
    variant: 'checkbox',
}) `
	${checkboxIconStyles}
`;
//# sourceMappingURL=checkbox.styled.js.map